import {Employee, HibobOffice, HibobResidence, Office, WorkSite} from "./Model";
import {useCurrentOfficeStore} from "./CurrentOfficeStore";
import {RemoteValue} from "../util/RemoteValue";
import {useOfficeStore} from "./OfficeStore";
import {useMemo} from "react";
import {useWorkSiteStore} from "./WorkSiteStore";
import {Opt} from "../util/Optional";
import {useHibobOfficeStore} from "./HibobOfficeStore";
import {groupBy, mapValues} from "../util/MapUtil";
import {stringCmp} from "../util/SortUtil";

export type Residence = {
    type: 'residents',
    currentOfficeResidents: readonly HibobResidence[],
} | {
    type: 'not-residents',
    currentOfficeResidents: readonly HibobResidence[],
} | {
    type: 'work-site',
    workSite: WorkSite,
} | {
    type: 'hibob-office',
    hibobOffice: HibobOffice,
};

export class ResidenceModel {
    readonly options: readonly Residence[];
    readonly officeResidents: readonly HibobResidence[];
    readonly residents: Residence & { type: 'residents' };
    readonly notResidents: Residence & { type: 'not-residents' };

    constructor(
        workSites: WorkSite[],
        hibobOffices: HibobOffice[],
        offices: Office[],
        currentOfficeId: Office["id"] | null,
    ) {
        this.officeResidents = offices
                ?.find(office => office.id === currentOfficeId)
                ?.hibobResidence
            ?? [];

        this.residents = {
            type: 'residents',
            currentOfficeResidents: this.officeResidents,
        };
        this.notResidents = {
            type: 'not-residents',
            currentOfficeResidents: this.officeResidents,
        };
        const residenceOptions: Residence[] = this.officeResidents.length > 0 ? [
            this.residents,
            this.notResidents,
        ] : [];

        const site2hibobOffice = mapValues(
            groupBy(
                hibobOffices,
                hibobOffice => hibobOffice.workSiteId,
            ),
            hibobOffices => hibobOffices
                .sort(stringCmp(hibobOffice => hibobOffice.name))
                .map(hibobOffice => ({
                    type: 'hibob-office' as const,
                    hibobOffice,
                })),
        );

        const workSiteTree: Residence[] = workSites
            .sort(stringCmp(workSite => workSite.name))
            .flatMap(workSite => [
                {
                    type: 'work-site' as const,
                    workSite,
                },
                ...(site2hibobOffice.get(workSite.id) ?? []),
            ]);

        const freeHangingOffices = site2hibobOffice.get(null) ?? [];
        this.options = [...residenceOptions, ...workSiteTree, ...freeHangingOffices];
    }

    isResident(employee: Pick<Employee, 'siteId' | 'hibobOfficeId'> | null | undefined) {
        return ResidenceModel.matchResidence(employee, this.officeResidents)
    }

    matchResidence(hibobResidence: HibobResidence) {
        return ResidenceModel.matchResidence({
            siteId: hibobResidence?.workSite?.id,
            hibobOfficeId: hibobResidence?.hibobOffice?.id,
        }, this.officeResidents)
    }


    static EMPTY = new ResidenceModel([], [], [], null);

    static matchResidence(
        employee: Opt<Pick<Employee, 'siteId' | 'hibobOfficeId'>>,
        officeResidents: readonly HibobResidence[],
    ): boolean {
        return employee != null && officeResidents.some(({workSite, hibobOffice}) =>
            (workSite != null || hibobOffice != null) &&
            (workSite == null || workSite.id === employee.siteId) &&
            (hibobOffice == null || hibobOffice.id === employee.hibobOfficeId)
        );
    }

    static serialize(residence: Residence): string {
        switch (residence.type) {
            case "residents":
                return 'residents';
            case "not-residents":
                return 'not-residents';
            case "work-site":
                return `work-site-${residence.workSite.id}`;
            case "hibob-office":
                return `hibob-office-${residence.hibobOffice.id}`;
        }
    }

    deserialize(value: string): Opt<Residence> {
        return this.options.find(residence => ResidenceModel.serialize(residence) === value);
    }
}

export function matchResidence(residency: Opt<Residence>): Opt<(employee: Pick<Employee, 'siteId' | 'hibobOfficeId'> | null | undefined) => boolean> {
    if (residency === undefined || residency === null) {
        return undefined;
    } else {
        return employee => {
            switch (residency.type) {
                case 'residents':
                    return ResidenceModel.matchResidence(employee, residency.currentOfficeResidents);
                case 'not-residents':
                    return !ResidenceModel.matchResidence(employee, residency.currentOfficeResidents);
                case 'work-site':
                    return employee?.siteId === residency.workSite.id;
                case "hibob-office":
                    return employee?.hibobOfficeId === residency.hibobOffice.id;
            }
        };
    }
}

export function useResidenceStore(): { remoteValue: RemoteValue<ResidenceModel> } {
    const currentOfficeId = useCurrentOfficeStore(state => state.officeId);
    const offices = useOfficeStore(state => state.remoteValue);
    const workSites = useWorkSiteStore(state => state.remoteValue);
    const hibobOffices = useHibobOfficeStore(state => state.remoteValue);

    return useMemo(() => ({
        remoteValue: RemoteValue
            .combine({offices, hibobOffices, workSites})
            .map(({offices, hibobOffices, workSites}) =>
                new ResidenceModel(workSites, hibobOffices, offices, currentOfficeId)
            )
    }), [currentOfficeId, offices, workSites]);
}
